import { useEffect, useState, useCallback } from "react";
import { api } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Loader2,
  Crown,
  Search,
  Calendar,
  Users,
  DollarSign,
  Lock,
  Unlock,
  Plus,
  Minus,
  RefreshCw,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const FILTERS = [
  { id: "all", label: "Todos" },
  { id: "vip_active", label: "VIP Activos" },
  { id: "vip_expiring_30d", label: "VIP por vencer (30d)" },
  { id: "premium_eligible", label: "Premium elegible" },
  { id: "locked", label: "Premium bloqueado (manual)" },
];

function StatTile({ icon: Icon, label, value, color = "amber" }) {
  return (
    <div className={`bg-${color}-50 border border-${color}-100 rounded-lg p-3`}>
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 text-${color}-600`} />
        <p className="text-xs text-neutral-600 uppercase tracking-wider">{label}</p>
      </div>
      <p className={`text-xl font-bold text-${color}-700 mt-1`}>{value}</p>
    </div>
  );
}

function ActionBtn({ onClick, busy, children, variant = "outline", testId }) {
  return (
    <Button
      size="sm"
      variant={variant}
      disabled={busy}
      onClick={onClick}
      className="text-xs h-7 px-2"
      data-testid={testId}
    >
      {children}
    </Button>
  );
}

export default function AdminVipPremiumTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [busyUserId, setBusyUserId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { filter };
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/admin/vip-premium/list", { params });
      setData(data);
    } catch (_e) {
      toast.error("No se pudo cargar la lista");
    } finally {
      setLoading(false);
    }
  }, [filter, q]);

  useEffect(() => {
    const t = setTimeout(fetchData, q ? 350 : 0);
    return () => clearTimeout(t);
  }, [fetchData, q]);

  const runAction = async (userId, action, extra = {}) => {
    setBusyUserId(userId);
    try {
      await api.post(`/admin/users/${userId}/premium-action`, { action, ...extra });
      toast.success("Acción aplicada");
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo aplicar la acción");
    } finally {
      setBusyUserId(null);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" });
    } catch {
      return String(iso).slice(0, 10);
    }
  };

  const fmtMoney = (n) => `$${Number(n || 0).toLocaleString("es-MX")}`;

  return (
    <div className="space-y-5" data-testid="admin-vip-premium-tab">
      {/* Header stats */}
      {data?.stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatTile icon={Crown} label="VIP Activos" value={data.stats.total_vip_active} color="amber" />
          <StatTile icon={AlertTriangle} label="Vencen en 30 días" value={data.stats.vip_expiring_30d} color="orange" />
          <StatTile icon={Users} label="Premium elegibles (vista)" value={data.stats.premium_unlocked_in_view} color="emerald" />
          <StatTile icon={DollarSign} label="Ingresos VIP anuales" value={fmtMoney(data.stats.vip_annual_revenue_mxn)} color="violet" />
        </div>
      )}

      {/* Filters + search */}
      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${
              filter === f.id
                ? "bg-agave text-white"
                : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
            }`}
            data-testid={`vip-filter-${f.id}`}
          >
            {f.label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Email, nombre, código…"
              className="pl-7 h-8 w-56 text-xs"
              data-testid="vip-search-input"
            />
          </div>
          <Button size="sm" variant="outline" onClick={fetchData} disabled={loading} className="h-8 px-2">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="card-base p-0 overflow-hidden">
        {loading && !data ? (
          <div className="p-10 text-center">
            <Loader2 className="w-6 h-6 animate-spin text-amber-500 mx-auto" />
          </div>
        ) : (data?.items?.length || 0) === 0 ? (
          <div className="p-10 text-center text-sm text-neutral-500" data-testid="vip-empty">
            Ningún usuario coincide con este filtro.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Usuario</TableHead>
                  <TableHead>Premium</TableHead>
                  <TableHead>VIP</TableHead>
                  <TableHead>Vence</TableHead>
                  <TableHead>Viajes</TableHead>
                  <TableHead>Código</TableHead>
                  <TableHead>Refs</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((u) => {
                  const busy = busyUserId === u.user_id;
                  return (
                    <TableRow key={u.user_id} data-testid={`vip-row-${u.user_id}`}>
                      <TableCell className="min-w-[180px]">
                        <div className="flex items-center gap-2">
                          {u.picture ? (
                            <img src={u.picture} alt="" className="w-7 h-7 rounded-full object-cover" />
                          ) : (
                            <div className="w-7 h-7 rounded-full bg-neutral-200" />
                          )}
                          <div className="min-w-0">
                            <p className="font-medium text-sm truncate max-w-[160px]">{u.name || u.email}</p>
                            <p className="text-[11px] text-neutral-500 truncate max-w-[160px]">{u.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {u.premium_admin_locked ? (
                          <Badge className="bg-red-100 text-red-800 border-red-200 gap-1">
                            <Lock className="w-3 h-3" />
                            Bloqueado
                          </Badge>
                        ) : u.premium_eligible ? (
                          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">
                            {u.premium_eligible_via === "vip_membership"
                              ? "VIP"
                              : u.premium_eligible_via === "completed_rentals"
                              ? "10+ rentas"
                              : "Override"}
                          </Badge>
                        ) : (
                          <Badge className="bg-neutral-100 text-neutral-600 border-neutral-200">Locked</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {u.vip_active ? (
                          <Badge className="bg-amber-500 text-white border-0 gap-1">
                            <Crown className="w-3 h-3" />
                            Activo
                          </Badge>
                        ) : (
                          <span className="text-xs text-neutral-400">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-neutral-700">{u.vip_active ? fmtDate(u.vip_expires_at) : "—"}</TableCell>
                      <TableCell className="text-center text-sm font-medium">{u.completed_rental_count ?? 0}</TableCell>
                      <TableCell>
                        {u.vip_referral_code ? (
                          <code className="text-[11px] font-mono bg-amber-50 text-amber-800 px-2 py-0.5 rounded">
                            {u.vip_referral_code}
                          </code>
                        ) : (
                          <span className="text-xs text-neutral-400">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center text-sm">{u.total_referrals}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex flex-wrap gap-1 justify-end">
                          {!u.vip_active ? (
                            <ActionBtn busy={busy} onClick={() => runAction(u.user_id, "grant_vip")} testId={`vip-grant-${u.user_id}`}>
                              <Plus className="w-3 h-3 mr-0.5" />VIP
                            </ActionBtn>
                          ) : (
                            <>
                              <ActionBtn busy={busy} onClick={() => runAction(u.user_id, "extend_vip", { extend_days: 30 })} testId={`vip-ext30-${u.user_id}`}>
                                +30d
                              </ActionBtn>
                              <ActionBtn busy={busy} onClick={() => runAction(u.user_id, "revoke_vip")} testId={`vip-revoke-${u.user_id}`}>
                                <Minus className="w-3 h-3" />
                              </ActionBtn>
                            </>
                          )}
                          {u.premium_admin_locked ? (
                            <ActionBtn busy={busy} onClick={() => runAction(u.user_id, "unlock_premium")} testId={`vip-unlock-${u.user_id}`}>
                              <Unlock className="w-3 h-3" />
                            </ActionBtn>
                          ) : (
                            <ActionBtn busy={busy} onClick={() => runAction(u.user_id, "lock_premium")} testId={`vip-lock-${u.user_id}`}>
                              <Lock className="w-3 h-3" />
                            </ActionBtn>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <p className="text-[11px] text-neutral-500 italic">
        Los códigos VIP se generan automáticamente al activar la membresía. Las acciones se reflejan en el panel de detalle del usuario y en cualquier reserva en curso.
      </p>
    </div>
  );
}
